/**
 * useDataCollectionBinding
 *
 * Low-level utility: watches a data collection reach "ready" status and
 * automatically creates a task binding with field mappings.
 *
 * Shared by IntegrationSyncWorkflow (Gmail, Todoist, Asana, …) and any
 * future integration that maps an external data source to Routine tasks.
 */

import type { MembraneClient } from "@membranehq/sdk";
import { watch } from "vue";
import type { Ref } from "vue";

import type {
  Cell,
  Controller,
  GraphDatabaseDictionaryDictionary,
  IntegrationAppEnabledDataCollection,
  IntegrationAppEnabledDataCollectionId,
  TaskBinding,
  TaskBindingDictionary,
} from "@ctrl/controller-rpc";

import { logger as rendererLogger } from "@/logger/renderer";
import {
  errorMessage,
  isRecord,
  optionalStringProperty,
  property,
} from "@/utils/unknown";

import type { WorkspaceDataItem } from "./useMembraneSetup";

const logger = rendererLogger.renderer.wrapperForModule(
  "useDataCollectionBinding"
);

export type StatusCellMapping = {
  done: { read: Cell[]; write: Cell };
  todo: { read: Cell[]; write: Cell };
  discarded: { read: Cell[]; write: Cell };
};

/**
 * Outcome of one attempt at resolving the status cells of a connection.
 *
 * The three cases exist to drive retry policy, which a bare
 * `StatusCellMapping | undefined` cannot: `failed` is worth asking again,
 * `unavailable` never is. Conflating them is what made a network blip and a
 * database with no completable property look identical, so both ended as a
 * binding with no status mapping and nothing to retry.
 *
 * A resolver may also just throw — {@link resolveStatusCellMapping} turns that
 * into `failed`, which is the right default for an unexpected error.
 */
export type StatusCellResolution =
  | { kind: "resolved"; cells: StatusCellMapping }
  /** The provider answered, and holds nothing that can carry a completion
   *  state. Terminal: asking again returns the same answer. */
  | { kind: "unavailable" }
  /** The lookup did not produce an answer. Retried when the error looks
   *  transient (see {@link isRetryableStatusCellError}). */
  | { kind: "failed"; error: unknown };

export type StatusCellRetryOptions = {
  /** Total attempts, first one included. */
  attempts?: number;
  /** Delay in ms before the attempt that follows `attempt` (1-based).
   *  Injectable so tests need not wait out the real backoff. */
  delayForAttempt?: (attempt: number) => number;
};

/** Attempts for a transient failure: the first try plus two retries. */
export const defaultStatusCellAttempts = 3;

/**
 * ~1s then ~3s, each jittered by up to a third, so a provider that just rate
 * limited a whole workspace does not get every client back in lockstep.
 */
export const defaultStatusCellDelay = (attempt: number): number => {
  const base = attempt === 1 ? 1_000 : 3_000;
  return Math.round(base * (1 + Math.random() / 3));
};

// Bare `setTimeout`, not `window.setTimeout`: this resolution path is unit
// tested outside a DOM environment, and the handle is never needed.
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Reads an HTTP status off the shapes Membrane and fetch errors use. */
function httpStatusOf(error: unknown): number | undefined {
  const candidates = [
    property(error, "status"),
    property(error, "statusCode"),
    property(property(error, "response"), "status"),
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isInteger(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

/**
 * Whether another attempt could plausibly succeed.
 *
 * No status at all means the request never reached the provider (offline, DNS,
 * a dropped socket), which is the case retrying exists for. 408 and 429 are
 * explicit invitations to try later, and 5xx is the provider apologising. Every
 * other 4xx is terminal: a revoked token (401), a connection that lost access
 * to the database (403) and a deleted database (404) answer the same however
 * many times we ask, so retrying only spends rate limit and delays the log
 * entry that tells the user what is actually wrong.
 */
export function isRetryableStatusCellError(error: unknown): boolean {
  const status = httpStatusOf(error);
  if (status === undefined) return true;
  if (status === 408 || status === 429) return true;
  return status >= 500;
}

/**
 * Why a watched data collection is not (yet) usable.
 *
 * `error` is terminal for this attempt: the API stored an error state on the
 * collection. `timeout` is not — the collection is still `creation_pending`,
 * the refresh job may simply be queued behind other work. Both exist so a
 * caller can stop showing an indefinite progress state; neither stops the
 * watch, so a collection that becomes ready later still gets its binding.
 */
export type DataCollectionFailure =
  | { kind: "error"; description: string }
  | { kind: "timeout" }
  | { kind: "binding"; description: string };

/**
 * How long a data collection may stay `creation_pending` before the caller is
 * told to stop blocking on it.
 *
 * The API's refresh spends up to ~63 s polling Membrane for a ready flow
 * instance on top of the record fetch, and the job itself waits for a poll
 * cycle first, so anything shorter reports healthy setups as stalled.
 */
export const dataCollectionReadyTimeoutMs = 90_000;

export type FieldNames = {
  /** Column name for url_mapping. */
  url?: string;
  /** Column name for scheduled_mapping. */
  scheduled?: string;
  /**
   * Column name for the status_mapping. Defaults to boolean cells.
   * Provide `customStatusCells` to use string (or other) cell values instead.
   */
  completed?: string;
  /** Override the default boolean cells for each task state. */
  customStatusCells?: StatusCellMapping;
  /**
   * Resolves the status cells for one connection, for integrations whose values
   * can only be known at bind time. Notion is the motivating case: a status
   * option's ID belongs to the specific database being synced, so it cannot be
   * written down here the way Todoist's `checked` booleans can.
   *
   * Takes precedence over `customStatusCells`. Report `unavailable` only when
   * the provider answered and genuinely holds no completable property: that
   * answer is trusted and never retried. Anything that might succeed later
   * should be `failed` — or simply thrown — so the retry in
   * {@link resolveStatusCellMapping} gets its turn.
   *
   * Once every attempt is spent, resolution falls back to `customStatusCells`,
   * then to boolean cells, so a transient failure never leaves the binding with
   * no status mapping at all. Set `requireResolvedStatusCells` to opt out of
   * that fallback.
   */
  resolveStatusCells?: (
    connectionId: string,
    parameters: Record<string, unknown>,
    membrane: MembraneClient
  ) => Promise<StatusCellResolution>;
  /**
   * When set, an unresolved mapping leaves the binding's status mapping unset
   * instead of falling back.
   *
   * Use it whenever the status column holds provider-specific values rather
   * than a boolean — Featurebase's `statusId` holds Featurebase status ids, so
   * the boolean fallback would write `true`/`false` into a string column. Every
   * status write would then fail against the provider while the binding looked
   * correctly configured. An unset mapping is the honest state: the task simply
   * has no completion mapping, and the console carries the reason.
   */
  requireResolvedStatusCells?: boolean;
};

/** Status cells for the common case of a boolean "done" column. */
export const booleanStatusCells = (): StatusCellMapping => {
  const cellTrue: Cell = { type: "bool", value: true };
  const cellFalse: Cell = { type: "bool", value: false };
  return {
    done: { read: [cellTrue], write: cellTrue },
    todo: { read: [cellFalse], write: cellFalse },
    discarded: { read: [], write: cellTrue },
  };
};

/**
 * Applies the fallback policy once a resolver has had its turn: the resolved
 * mapping wins, then static cells, then boolean defaults — unless the
 * integration set `requireResolvedStatusCells`, in which case there is no
 * fallback and the mapping stays unset.
 *
 * Split out from `resolveStatusCellMapping` so the policy is testable without a
 * live Membrane client.
 */
export function statusCellsOrFallback(
  fields: FieldNames,
  resolved: StatusCellMapping | undefined
): StatusCellMapping | undefined {
  if (resolved) return resolved;
  if (fields.requireResolvedStatusCells) return undefined;
  return fields.customStatusCells ?? booleanStatusCells();
}

/**
 * Runs `resolve` until it produces an answer, retrying only failures that look
 * transient. Returns the last failure once the attempts are spent.
 */
export async function resolveStatusCellsWithRetry(
  resolve: () => Promise<StatusCellResolution>,
  options: StatusCellRetryOptions = {},
  /** Included in the retry logs, so a noisy provider is identifiable. */
  context: Record<string, unknown> = {}
): Promise<StatusCellResolution> {
  const attempts = Math.max(1, options.attempts ?? defaultStatusCellAttempts);
  const delayForAttempt = options.delayForAttempt ?? defaultStatusCellDelay;
  let lastFailure: StatusCellResolution | undefined;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    let failure: { kind: "failed"; error: unknown };
    try {
      const resolution = await resolve();
      if (resolution.kind !== "failed") return resolution;
      failure = resolution;
    } catch (error) {
      failure = { kind: "failed", error };
    }
    lastFailure = failure;

    if (!isRetryableStatusCellError(failure.error)) {
      logger.warn("Status cell resolution failed for good", {
        ...context,
        attempt,
        error: failure.error,
      });
      return failure;
    }
    if (attempt === attempts) break;

    const delay = delayForAttempt(attempt);
    logger.info("Retrying status cell resolution after a transient failure", {
      ...context,
      attempt,
      attempts,
      delayMs: delay,
      error: failure.error,
    });
    await sleep(delay);
  }

  // `attempts` is at least 1, so the loop always ran and always assigned.
  return lastFailure ?? { kind: "unavailable" };
}

/**
 * Resolves the status cells to write into a task binding, preferring a
 * per-connection resolver over static cells over boolean defaults.
 *
 * Returns `undefined` only for integrations that require a resolved mapping;
 * the caller then omits `status_mapping` entirely.
 */
export async function resolveStatusCellMapping(
  fields: FieldNames,
  connectionId: string,
  parameters: Record<string, unknown>,
  membrane: MembraneClient | undefined,
  options?: StatusCellRetryOptions
): Promise<StatusCellMapping | undefined> {
  let resolution: StatusCellResolution = { kind: "unavailable" };

  if (fields.resolveStatusCells && membrane) {
    // Captured before the closure so the optional-property narrowing holds.
    const resolve = fields.resolveStatusCells;
    resolution = await resolveStatusCellsWithRetry(
      () => resolve(connectionId, parameters, membrane),
      options,
      { connectionId }
    );
  }

  const cells = statusCellsOrFallback(
    fields,
    resolution.kind === "resolved" ? resolution.cells : undefined
  );
  if (!cells) {
    // Loud on purpose: the sync is otherwise healthy, so nothing else would
    // reveal that completing a task cannot write back to the provider.
    logger.error(
      "Leaving the task binding status mapping unset: status cells could not " +
        "be resolved and this integration has no safe fallback",
      {
        connectionId,
        resolution: resolution.kind,
        ...(resolution.kind === "failed" ? { error: resolution.error } : {}),
      }
    );
  }
  return cells;
}

/**
 * Compares submitted enableDataCollection parameters (plain strings) with the
 * parameters stored on an enabled data collection id. Stored values may be
 * plain strings or Cell-wrapped ({ type, value }), so both forms are accepted.
 */
export function sameDataCollectionParameters(
  submitted: Record<string, string>,
  stored: unknown
): boolean {
  const storedRecord: Record<string, unknown> = isRecord(stored) ? stored : {};
  const submittedKeys = Object.keys(submitted);
  if (Object.keys(storedRecord).length !== submittedKeys.length) return false;
  return submittedKeys.every((key) => {
    const cell = storedRecord[key];
    const value =
      typeof cell === "string" ? cell : optionalStringProperty(cell, "value");
    return value === submitted[key];
  });
}

/**
 * Creates the binding for one ready data collection, using its owning workspace
 * and the integration's resolved field mappings.
 *
 * `watchForDataCollectionReady` matches on parameters (not just connection +
 * collection key) because
 * an account has several syncs of the same collection: an already-ready
 * sibling sync must not satisfy the watch for a newly created one.
 *
 */
const bindingSetups = new Map<string, Promise<void>>();

export function ensureDataCollectionBinding(
  workspace: WorkspaceDataItem,
  controller: Controller,
  workspaceDatabases: Ref<GraphDatabaseDictionaryDictionary | undefined>,
  dataCollectionId: IntegrationAppEnabledDataCollectionId,
  dataCollection: Extract<
    IntegrationAppEnabledDataCollection,
    { status: "ready" }
  >,
  fields: FieldNames,
  getMembrane: () => MembraneClient | undefined,
  resetExisting = false
): Promise<void> {
  const key = `${workspace.workspaceId}:${dataCollection.table}`;
  const existing = bindingSetups.get(key);
  if (existing) return existing;

  const setup = ensureDataCollectionBindingNow(
    workspace,
    controller,
    workspaceDatabases,
    dataCollectionId,
    dataCollection,
    fields,
    getMembrane,
    resetExisting
  );
  bindingSetups.set(key, setup);
  void setup.then(
    () => {
      if (bindingSetups.get(key) === setup) bindingSetups.delete(key);
    },
    () => {
      if (bindingSetups.get(key) === setup) bindingSetups.delete(key);
    }
  );
  return setup;
}

async function ensureDataCollectionBindingNow(
  workspace: WorkspaceDataItem,
  controller: Controller,
  workspaceDatabases: Ref<GraphDatabaseDictionaryDictionary | undefined>,
  dataCollectionId: IntegrationAppEnabledDataCollectionId,
  dataCollection: Extract<
    IntegrationAppEnabledDataCollection,
    { status: "ready" }
  >,
  fields: FieldNames,
  /**
   * Read lazily: the collection becomes ready long after this call, and only
   * `fields.resolveStatusCells` needs the client.
   */
  getMembrane: () => MembraneClient | undefined,
  resetExisting: boolean
): Promise<void> {
  const dbFields =
    workspaceDatabases.value?.[workspace.workspaceId]?.[dataCollection.table]
      ?.type?.fields;
  // The table and its schema propagate separately from the collection's ready
  // state. The app-lifetime repair watches both and retries when the schema
  // arrives; creating a binding before then would leave it half-configured.
  if (!dbFields || Object.keys(dbFields).length === 0) return;

  const bindingsRef = await controller.task.binding.all();
  const bindings: TaskBindingDictionary = bindingsRef.release() ?? {};
  const existingBinding = Object.entries(bindings).find(
    (binding) =>
      binding[1].workspace_id === workspace.workspaceId &&
      binding[1].table_id === dataCollection.table
  );

  if (existingBinding && !resetExisting) return;

  const bindingId =
    existingBinding?.[0] ??
    (await controller.workspace.taskBindings.add(
      workspace.workspaceId,
      dataCollection.table,
      {
        workspace_id: workspace.workspaceId,
        table_id: dataCollection.table,
      }
    ));

  const upsertMappings = async (existing: TaskBinding | undefined) => {
    const findColumnId = (name: string): string | undefined =>
      Object.entries(dbFields).find(
        ([, field]) => field.name.toLowerCase() === name.toLowerCase()
      )?.[0];

    const urlColumnId = fields.url ? findColumnId(fields.url) : undefined;
    const scheduledColumnId = fields.scheduled
      ? findColumnId(fields.scheduled)
      : undefined;
    const completedColumnId = fields.completed
      ? findColumnId(fields.completed)
      : undefined;

    const statusCells = await resolveStatusCellMapping(
      fields,
      dataCollectionId.connection_id,
      dataCollectionId.parameters,
      getMembrane()
    );

    // Follow the same pattern as TaskBindings.vue: spread the existing
    // config (stripping `rows` which the upsert RPC doesn't accept) so we
    // never overwrite mappings the user may have set manually.
    const { rows: _rows, ...existingConfig } = existing ?? {
      workspace_id: workspace.workspaceId,
      table_id: dataCollection.table,
    };

    const statusMapping =
      completedColumnId && statusCells
        ? {
            kind: "values" as const,
            column: completedColumnId,
            todo: statusCells.todo,
            done: statusCells.done,
            discarded: statusCells.discarded,
          }
        : undefined;

    await controller.task.binding.upsert(bindingId, {
      ...existingConfig,
      // `statusCells` is undefined when the integration requires a resolved
      // mapping and resolution failed. Automatic setup preserves an existing
      // mapping; an explicit reset clears it rather than writing invalid cells.
      ...(resetExisting
        ? {
            url_mapping: urlColumnId,
            scheduled_mapping: scheduledColumnId,
            status_mapping: statusMapping,
          }
        : {
            ...(urlColumnId ? { url_mapping: urlColumnId } : {}),
            ...(scheduledColumnId
              ? { scheduled_mapping: scheduledColumnId }
              : {}),
            ...(statusMapping ? { status_mapping: statusMapping } : {}),
          }),
    });
  };

  await upsertMappings(existingBinding?.[1]);
}

/**
 * Watches one workspace's collection until it is ready, then creates its task
 * binding. It also inspects the current state so a fast collection creation is
 * not missed before the watcher is registered.
 */
export function watchForDataCollectionReady(
  workspace: WorkspaceDataItem,
  controller: Controller,
  workspaceDatabases: Ref<GraphDatabaseDictionaryDictionary | undefined>,
  connectionId: string,
  collectionKey: string,
  matchesParameters: (parameters: unknown) => boolean,
  fields: FieldNames,
  /**
   * Read lazily: the collection becomes ready long after this call, and only
   * `fields.resolveStatusCells` needs the client.
   */
  getMembrane: () => MembraneClient | undefined,
  onReady: () => void,
  onFailure: (failure: DataCollectionFailure) => void = () => {}
): () => void {
  let settled = false;
  let stopped = false;
  let stop = () => {};
  const settle = (failure?: DataCollectionFailure) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeout);
    if (failure) onFailure(failure);
  };
  // `settle` reads `timeout`, which is initialized right here — before the
  // timer can fire and before the (never immediate) watcher below is even
  // registered, so it is never read in its temporal dead zone.
  const timeout = window.setTimeout(
    () => settle({ kind: "timeout" }),
    dataCollectionReadyTimeoutMs
  );

  const handleData = async (data: typeof workspace.membraneData.value) => {
    if (stopped) return;
    if (!data) return;

    // Using .find() instead of a for-of loop so we process exactly one match
    // per watcher firing — avoids calling onReady() twice if the collection
    // map somehow contains two matching entries.
    const entry = data.enabled_data_collections.find(
      ([id, dc]) =>
        id.connection_id === connectionId &&
        id.data_collection_key === collectionKey &&
        matchesParameters(id.parameters) &&
        (dc.status === "ready" || dc.status === "error")
    );

    if (!entry) return;

    const [dataCollectionId, dataCollection] = entry;

    if (dataCollection.status === "error") {
      // Keep watching: the next refresh can clear the error state, and this
      // watcher is the only thing that would then create the binding.
      settle({ kind: "error", description: dataCollection.description });
      return;
    }

    stop();
    settle();

    // Narrow the discriminated union: .find() can't propagate the
    // dataCollection.status === "ready" guard from its callback into the
    // return type.
    if (dataCollection.status !== "ready") return;

    try {
      await ensureDataCollectionBinding(
        workspace,
        controller,
        workspaceDatabases,
        dataCollectionId,
        dataCollection,
        fields,
        getMembrane
      );
      if (!stopped) onReady();
    } catch (error) {
      onFailure({ kind: "binding", description: errorMessage(error) });
    }
  };
  stop = watch(workspace.membraneData, handleData);
  // The collection can become ready before the watcher is registered. Queue
  // the first inspection so `stop` is initialized before it can settle.
  void Promise.resolve().then(() => handleData(workspace.membraneData.value));

  return () => {
    stopped = true;
    stop();
    settle();
  };
}
